import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Play } from "lucide-react";
import { api } from "../api";
import { useAuth } from "../auth";
import { mediaUrl, fmtDur } from "../config";
import Player from "../components/Player";
import { SeriesCard, Avatar, Spinner, Empty, SectionTitle } from "../components/cards";
import { orderFeed } from "../subscriptions";

export const SeriesList = () => {
  const { user } = useAuth();
  const [series, setSeries] = useState(null);
  useEffect(() => {
    api.get("/series?limit=60").then((r) => setSeries(Array.isArray(r) ? r : r.series || [])).catch(() => setSeries([]));
  }, []);

  const feed = useMemo(() => (series ? orderFeed(series, user) : null), [series, user]);
  if (series === null || feed === null) return <Spinner />;
  if (series.length === 0) return <Empty>No series yet.</Empty>;
  const list = [...(feed.fromSubs || []), ...(feed.rest || [])];

  return (
    <div className="mx-auto max-w-7xl" data-testid="series-page">
      <h1 className="mb-6 font-heading text-2xl font-bold tracking-tight text-white">Series</h1>
      {feed.personalized && feed.fromSubs.length > 0 && (
        <section className="mb-8" data-testid="series-from-subscriptions">
          <SectionTitle>From your subscriptions</SectionTitle>
          <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 xl:grid-cols-5">
            {feed.fromSubs.map((s) => (
              <SeriesCard key={s.id} series={s} />
            ))}
          </div>
        </section>
      )}
      {feed.personalized && <SectionTitle>More series</SectionTitle>}
      <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 xl:grid-cols-5" data-testid="series-grid">
        {(feed.personalized ? feed.rest : list).map((s) => (
          <SeriesCard key={s.id} series={s} />
        ))}
      </div>
      <p className="mt-6 text-xs text-slate-600">All series episodes are free to watch on the web.</p>
    </div>
  );
};

export const SeriesDetail = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [current, setCurrent] = useState(null);

  useEffect(() => {
    setData(null);
    setCurrent(null);
    api.get(`/series/${id}`).then((r) => {
      const series = r.series ?? r;
      const episodes = r.episodes ?? series.episodes ?? [];
      setData({ ...series, episodes });
      if (episodes[0]) setCurrent(episodes[0]);
    }).catch(() => setData({ episodes: [] }));
  }, [id]);

  if (data === null) return <Spinner />;

  const episodes = [...(data.episodes || [])].sort(
    (a, b) => (a.seasonNumber ?? 0) - (b.seasonNumber ?? 0) || (a.episodeNumber ?? 0) - (b.episodeNumber ?? 0)
  );

  return (
    <div className="mx-auto max-w-5xl" data-testid="series-detail-page">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-bold tracking-tight text-white" data-testid="series-title">{data.title}</h1>
        {data.creator && (
          <Link to={`/app/creator/${data.creator.id || data.creatorId}`} className="mt-2 flex items-center gap-2 text-sm text-slate-400 hover:text-white">
            <Avatar user={data.creator} size={24} />
            {data.creator.displayName || data.creator.username}
          </Link>
        )}
        {data.description && <p className="mt-3 text-sm leading-relaxed text-slate-400">{data.description}</p>}
      </div>

      {current && (
        <div className="mb-6">
          <Player video={current} className="aspect-video w-full rounded-2xl" />
          <p className="mt-3 font-heading text-sm font-semibold text-white" data-testid="now-playing-episode">
            {current.seasonNumber != null ? `S${current.seasonNumber} · E${current.episodeNumber} — ` : ""}{current.title}
          </p>
        </div>
      )}

      <h2 className="mb-3 font-heading text-base font-bold text-white">Episodes</h2>
      {episodes.length === 0 ? (
        <Empty>No episodes yet.</Empty>
      ) : (
        <div className="space-y-2">
          {episodes.map((ep) => (
            <button
              key={ep.id}
              onClick={() => setCurrent(ep)}
              data-testid={`episode-${ep.id}`}
              className={`flex w-full items-center gap-4 rounded-xl border p-3 text-left transition-colors ${current?.id === ep.id ? "border-crimson/50 bg-crimson/10" : "border-white/10 bg-white/[0.02] hover:border-white/25"}`}
            >
              <div className="relative h-16 w-28 shrink-0 overflow-hidden rounded-lg bg-surface">
                {mediaUrl(ep.thumbnail) && <img src={mediaUrl(ep.thumbnail)} alt="" className="h-full w-full object-cover" />}
                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                  <Play size={16} className="text-white" fill="white" />
                </div>
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-heading text-sm font-semibold text-white">
                  {ep.seasonNumber != null ? `S${ep.seasonNumber} · E${ep.episodeNumber} — ` : ""}{ep.title}
                </p>
                {ep.durationSec > 0 && <p className="font-mono text-[10px] text-slate-500">{fmtDur(ep.durationSec)}</p>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
